import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, of, Subject } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil } from "rxjs/operators";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { DashboardService } from "../../../services/dashboard.service";

type AnyObj = { [key: string]: any };

interface CalaReportResponse {
  name?: string;
  details?: AnyObj[];
  overview?: AnyObj;
  percentage?: AnyObj;
}

interface ReportLoadFailure {
  message: string;
}

interface KpiItem {
  icon: string;
  color: "blue" | "green" | "red" | "purple";
  label: string;
  value: string;
}

interface ProgressRow {
  icon: string;
  stage: string;
  description: string;
  expected: string;
  achieved: string;
  percentage: string;
  percentValue: number;
  status: string;
}

interface SummarySection {
  title: string;
  className: string;
  columns: string[];
  rows: string[][];
}

interface ProjectMetric {
  label: string;
  value: string;
}

@Component({
  selector: "app-cala-report",
  templateUrl: "./cala-report.component.html",
  styleUrls: [
    "../dashboard-report/dashboard-report.component.css",
    "./cala-report.component.css",
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalaReportComponent implements OnInit, OnDestroy {
  @ViewChild("pdfRoot", { static: false }) pdfRoot!: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();
  private autoDownloadStarted = false;
  private autoDownloadTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pdfPageRenderTimeoutMs = 30000;
  private readonly pdfRenderScale = 2;
  private readonly pdfImageQuality = 0.95;

  isLoading = true;
  isPdfGenerating = false;
  reportUnavailable = false;
  reportErrorMessage = "";
  dataWarning = "";
  autoDownloadMessage = "";
  autoDownload = true;
  reportKind = "CALA";

  calaId = "";
  calaName = "CALA Report";
  reportPeriod = "";
  today = new Date();

  overviewData: AnyObj = {};
  toBeCompleted: AnyObj = {};
  completedTillNow: AnyObj = {};
  inPercentage: AnyObj = {};
  projects: AnyObj[] = [];

  kpis: KpiItem[] = [];
  progressRows: ProgressRow[] = [];
  summarySections: SummarySection[] = [];
  projectPages: AnyObj[][] = [];

  constructor(
    private route: ActivatedRoute,
    protected dashboardService: DashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, queryParams]) => ({
          id: params.get("id") || "",
          reportDate: queryParams.get("reportDate") || "",
          autoDownload: queryParams.get("autoDownload") !== "false",
        })),
        filter(({ id }) => !!id),
        switchMap(({ id, reportDate, autoDownload }) => {
          this.calaId = id;
          this.reportPeriod = reportDate;
          this.autoDownload = autoDownload;
          this.isLoading = true;
          this.reportUnavailable = false;
          this.reportErrorMessage = "";
          this.dataWarning = "";
          this.autoDownloadMessage = autoDownload
            ? `Preparing ${this.reportKind} report download...`
            : "";
          this.autoDownloadStarted = false;
          this.clearAutoDownloadTimer();
          this.cdr.markForCheck();

          return this.loadDashboardData(id).pipe(
            catchError((error) =>
              of({
                message: this.extractErrorMessage(error),
              } as ReportLoadFailure)
            )
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: CalaReportResponse | ReportLoadFailure) => {
        if (this.isReportLoadFailure(response)) {
          this.reportUnavailable = true;
          this.reportErrorMessage = response.message;
          this.autoDownloadMessage = "";
          this.resetReportData();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.notifyAutoDownloadParent("error", response.message);
          return;
        }

        if (response && (response as AnyObj).detail) {
          this.reportUnavailable = true;
          this.reportErrorMessage = this.text(
            (response as AnyObj).detail,
            `${this.reportKind} report data could not be loaded.`
          );
          this.autoDownloadMessage = "";
          this.resetReportData();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.notifyAutoDownloadParent("error", this.reportErrorMessage);
          return;
        }

        this.bindReport(response as CalaReportResponse);
        this.isLoading = false;
        this.autoDownloadMessage = this.autoDownload
          ? "Preparing PDF download..."
          : "";
        this.cdr.markForCheck();

        if (this.autoDownload) {
          this.scheduleAutoDownload();
        }
      });
  }

  protected loadDashboardData(id: string) {
    return this.dashboardService.getCalaDashboardData(id);
  }

  private bindReport(report: CalaReportResponse): void {
    this.calaName = this.text(report.name, `${this.reportKind} Report`);
    this.overviewData = this.normalizeOverview(report.overview || {});
    this.toBeCompleted = this.normalizeBucket(
      this.value(this.overviewData, ["to_be_completed", "toBeCompleted"], {})
    );
    this.completedTillNow = this.normalizeBucket(
      this.value(
        this.overviewData,
        ["total_completed_till_now", "completed_till_now", "completedTillNow"],
        {}
      )
    );
    this.inPercentage = report.percentage || {};
    this.projects = Array.isArray(report.details) ? [...report.details] : [];
    this.projects.sort((a, b) =>
      this.reportItemName(a).localeCompare(this.reportItemName(b))
    );

    this.kpis = this.buildKpis();
    this.progressRows = this.buildProgressRows();
    this.summarySections = this.buildSummarySections();
    this.projectPages = this.chunk(this.projects, 2);
  }

  private buildKpis(): KpiItem[] {
    return [
      {
        icon: "fa-building",
        color: "purple",
        value: this.totalProjectsText(),
        label: "Total Projects",
      },
      {
        icon: "fa-clock-o",
        color: "blue",
        value: this.integer(this.overviewData.ontime),
        label: "On Time",
      },
      {
        icon: "fa-exclamation-triangle",
        color: "red",
        value: this.integer(this.overviewData.delayed),
        label: "Delayed",
      },
      {
        icon: "fa-check",
        color: "green",
        value: this.integer(this.overviewData.completed),
        label: "Completed",
      },
    ];
  }

  private buildProgressRows(): ProgressRow[] {
    return [
      this.progressRow(
        "fa-list-alt",
        "JMS",
        "Joint measurement survey completed",
        "jms",
        "percentage_jms"
      ),
      this.progressRow(
        "fa-file-text-o",
        "3(A)",
        "Preliminary notification completed",
        ["3A", "3a"],
        ["percentage_3A", "percentage_3a"]
      ),
      this.progressRow(
        "fa-users",
        "3(C)",
        "Claims and objections completed",
        "3C",
        "percentage_3C"
      ),
      this.progressRow(
        "fa-gavel",
        "3(D)",
        "Declaration completed",
        "3D",
        "percentage_3D"
      ),
      this.progressRow(
        "fa-inr",
        "3(G)",
        "Compensation determination completed",
        "3G",
        "percentage_3G"
      ),
      this.progressRow(
        "fa-map-marker",
        "3(H)",
        "Land acquisition completed",
        "3H_area",
        "percentage_3H_area"
      ),
    ];
  }

  private progressRow(
    icon: string,
    stage: string,
    description: string,
    valueKeys: string | string[],
    percentageKeys: string | string[]
  ): ProgressRow {
    const expectedNumber = this.num(this.value(this.toBeCompleted, valueKeys));
    const achievedNumber = this.num(
      this.value(this.completedTillNow, valueKeys)
    );
    const percentage = this.percent(
      this.value(
        this.inPercentage,
        percentageKeys,
        expectedNumber ? (achievedNumber / expectedNumber) * 100 : 0
      )
    );

    return {
      icon,
      stage,
      description,
      expected: this.number(expectedNumber),
      achieved: this.number(achievedNumber),
      percentage,
      percentValue: Math.min(100, Math.max(0, this.percentNumber(percentage))),
      status: this.progressStatus(expectedNumber, achievedNumber),
    };
  }

  private buildSummarySections(): SummarySection[] {
    return [
      {
        title: `${this.reportKind.toUpperCase()} STATUS`,
        className: "blue-block",
        columns: ["Item", "Value"],
        rows: [
          [this.reportKind, this.calaName],
          ["Reporting Period", this.reportPeriod || "Current"],
          ["Total Projects", this.totalProjectsText()],
          ["On Time", this.integer(this.overviewData.ontime)],
          ["Delayed", this.integer(this.overviewData.delayed)],
          ["Completed", this.integer(this.overviewData.completed)],
          [
            "Total Area To Be Acquired",
            `${this.number(this.overviewData.total_land_aquire)} Ha`,
          ],
        ],
      },
      {
        title: "LAND ACQUISITION PROGRESS",
        className: "green-block",
        columns: [
          "Item",
          "To Be Completed",
          "Completed Till Now",
          "In Percentage",
        ],
        rows: [
          this.summaryRow("3(a) Requests", "3a", "percentage_3a", false),
          this.summaryRow(
            "JMS Requests",
            "jms_request",
            "percentage_jms_request",
            false
          ),
          this.summaryRow("JMS", "jms", "percentage_jms"),
          this.summaryRow("3(A)", ["3A", "3a"], [
            "percentage_3A",
            "percentage_3a",
          ]),
          this.summaryRow("3(C)", "3C", "percentage_3C"),
          this.summaryRow("3(D)", "3D", "percentage_3D"),
          this.summaryRow("3(G)", "3G", "percentage_3G"),
          this.summaryRow("Land Acquired Under 3(H)", "3H_area", "percentage_3H_area"),
          this.summaryRow("Possession", "possession", "percentage_possession"),
          this.summaryRow("Mutation", "mutation", "percentage_mutation"),
        ],
      },
      {
        title: "FUNDS, GOVERNMENT LAND & PRIVATE NEGOTIATION",
        className: "orange-block",
        columns: [
          "Item",
          "To Be Completed",
          "Completed Till Now",
          "In Percentage",
        ],
        rows: [
          this.summaryRow(
            "Funds Distributed Under 3(H)",
            "3H_money",
            "percentage_3H_money"
          ),
          this.summaryRow(
            "Govt. Land Possession",
            "govt_possession",
            "percentage_govt_possession"
          ),
          this.summaryRow(
            "Govt. Land Mutation",
            "govt_mutation",
            "percentage_govt_mutation"
          ),
          [
            "Area to be acquired by PN",
            this.number(this.overviewData.total_private_land_aquired),
            this.number(this.completedTillNow.private_land_aquired),
            this.percent(this.inPercentage.percentage_private_land_aquired),
          ],
          [
            "PN fund deposition",
            this.number(this.overviewData.total_private_fund_deposition),
            this.number(this.completedTillNow.private_fund_deposition),
            this.percent(this.inPercentage.percentage_private_fund_deposition),
          ],
          [
            "PN fund distribution",
            this.number(this.overviewData.total_private_fund_distribution),
            this.number(this.completedTillNow.private_fund_distribution),
            this.percent(
              this.inPercentage.percentage_private_fund_distribution
            ),
          ],
        ],
      },
    ];
  }

  private summaryRow(
    label: string,
    valueKeys: string | string[],
    percentageKeys: string | string[],
    useDecimals = true
  ): string[] {
    const expected = this.value(this.toBeCompleted, valueKeys);
    const achieved = this.value(this.completedTillNow, valueKeys);

    return [
      label,
      useDecimals ? this.number(expected) : this.integer(expected),
      useDecimals ? this.number(achieved) : this.integer(achieved),
      this.percent(this.value(this.inPercentage, percentageKeys)),
    ];
  }

  projectMetrics(project: AnyObj): ProjectMetric[] {
    return [
      { label: "JMS Completed", value: this.number(project?.jms) },
      { label: "3(A) Completed", value: this.number(project?.["3A"]) },
      { label: "3(C) Completed", value: this.number(project?.["3C"]) },
      { label: "3(D) Completed", value: this.number(project?.["3D"]) },
      { label: "3(G) Completed", value: this.number(project?.["3G"]) },
      { label: "3(H) Completed", value: this.number(project?.["3H_area"]) },
      {
        label: "Funds Distribution Under 3(H)",
        value: this.number(project?.["3H_money"]),
      },
      { label: "Possession of Land", value: this.number(project?.possession) },
      { label: "Mutation of Land", value: this.number(project?.mutation) },
      {
        label: "Govt. Land To Be Acquired",
        value: this.number(project?.govt_land_aquire),
      },
      {
        label: "Govt. Land Possession",
        value: this.number(project?.govt_possession),
      },
      {
        label: "Govt. Land Mutation",
        value: this.number(project?.govt_mutation),
      },
      {
        label: "Total Area To Be Acquired By PN",
        value: this.number(project?.total_private_land_aquired),
      },
      {
        label: "Area Actually Acquired Under PN",
        value: this.number(project?.private_land_aquired),
      },
      {
        label:
          this.reportKind === "CALA"
            ? "Total Private Fund Disbursed"
            : "Total Private Fund Deposition",
        value: this.number(
          project?.total_private_fund_disbursed ??
            project?.total_private_fund_deposition
        ),
      },
      {
        label: "Funds Deposition Under PN",
        value: this.number(project?.private_fund_deposition),
      },
      {
        label: "Funds Distribution Under PN",
        value: this.number(project?.private_fund_distribution),
      },
    ];
  }

  splitMetrics(
    metrics: ProjectMetric[],
    side: "left" | "right"
  ): ProjectMetric[] {
    const middle = Math.ceil(metrics.length / 2);
    return side === "left"
      ? metrics.slice(0, middle)
      : metrics.slice(middle);
  }

  reportItemName(item: AnyObj): string {
    return this.text(item?.project_name || item?.name, "N/A");
  }

  get totalAreaText(): string {
    return this.number(this.overviewData.total_land_aquire);
  }

  get acquiredAreaText(): string {
    return this.number(this.completedTillNow["3H_area"]);
  }

  get remainingAreaText(): string {
    return Math.max(
      this.num(this.overviewData.total_land_aquire) -
        this.num(this.completedTillNow["3H_area"]),
      0
    ).toFixed(2);
  }

  get overallAreaProgressText(): string {
    const total = this.num(this.overviewData.total_land_aquire);
    const acquired = this.num(this.completedTillNow["3H_area"]);
    return total ? `${((acquired / total) * 100).toFixed(2)}%` : "0.00%";
  }

  get reportTotalPages(): number {
    return 2 + this.projectPages.length;
  }

  statusClass(status: string): string {
    return status.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  }

  trackByIndex(index: number): number {
    return index;
  }

  async downloadPdf(): Promise<void> {
    if (
      this.reportUnavailable ||
      !this.pdfRoot ||
      this.isPdfGenerating
    ) {
      return;
    }

    const root = this.pdfRoot.nativeElement;
    this.isPdfGenerating = true;
    this.autoDownloadMessage = `Generating ${this.reportKind} PDF...`;
    this.cdr.markForCheck();

    try {
      root.classList.add("pdf-mode");
      await this.waitForFonts();
      await this.waitForImages(root);

      const pages = Array.from(
        root.querySelectorAll(".pdf-page")
      ) as HTMLElement[];
      if (!pages.length) {
        throw new Error(`No ${this.reportKind} report pages found.`);
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();

      for (let index = 0; index < pages.length; index++) {
        this.autoDownloadMessage = `Generating PDF page ${index + 1} of ${
          pages.length
        }...`;
        this.cdr.detectChanges();
        await this.nextPaint();
        const canvas = await this.renderPdfPage(pages[index]);

        if (index > 0) {
          pdf.addPage();
        }
        pdf.addImage(
          canvas.toDataURL("image/jpeg", this.pdfImageQuality),
          "JPEG",
          0,
          0,
          width,
          height
        );
        canvas.width = 0;
        canvas.height = 0;
      }

      this.autoDownloadMessage = "Starting PDF download...";
      this.cdr.detectChanges();
      await this.nextPaint();
      pdf.save(this.getPdfFileName());
      this.autoDownloadMessage = "";
      this.notifyAutoDownloadParent("complete");
    } catch (error) {
      this.autoDownloadMessage = "";
      this.dataWarning =
        `Unable to download the ${this.reportKind} PDF. Please refresh the report and try again.`;
      console.error(`${this.reportKind} report PDF generation failed`, error);
      this.notifyAutoDownloadParent("error", this.dataWarning);
    } finally {
      root.classList.remove("pdf-mode");
      this.isPdfGenerating = false;
      this.cdr.markForCheck();
    }
  }

  private notifyAutoDownloadParent(
    status: "complete" | "error",
    message = ""
  ): void {
    if (!this.autoDownload || window.parent === window) {
      return;
    }

    window.parent.postMessage(
      { type: "nhai-report-auto-download", status, message },
      window.location.origin
    );
  }

  private renderPdfPage(page: HTMLElement): Promise<HTMLCanvasElement> {
    const renderPromise = html2canvas(page, {
      backgroundColor: "#ffffff",
      scale: this.pdfRenderScale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 10000,
      removeContainer: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: page.scrollWidth,
      windowHeight: page.scrollHeight,
    });
    const timeoutPromise = new Promise<HTMLCanvasElement>((_, reject) => {
      setTimeout(
        () =>
          reject(
            new Error(
              `Timed out while rendering a ${this.reportKind} PDF page.`
            )
          ),
        this.pdfPageRenderTimeoutMs
      );
    });

    return Promise.race([renderPromise, timeoutPromise]);
  }

  private scheduleAutoDownload(): void {
    if (this.autoDownloadStarted || this.reportUnavailable) {
      return;
    }

    this.clearAutoDownloadTimer();
    this.autoDownloadTimer = setTimeout(
      () => this.tryAutoDownload(0),
      1000
    );
  }

  private tryAutoDownload(attempt: number): void {
    const hasPages = !!this.pdfRoot?.nativeElement?.querySelector(".pdf-page");

    if (!this.isLoading && !this.isPdfGenerating && hasPages) {
      this.autoDownloadStarted = true;
      this.downloadPdf();
      return;
    }

    if (attempt >= 20 || this.autoDownloadStarted) {
      return;
    }

    this.autoDownloadTimer = setTimeout(
      () => this.tryAutoDownload(attempt + 1),
      500
    );
  }

  private clearAutoDownloadTimer(): void {
    if (this.autoDownloadTimer) {
      clearTimeout(this.autoDownloadTimer);
      this.autoDownloadTimer = null;
    }
  }

  private getPdfFileName(): string {
    const fallbackName = `${this.reportKind.toLowerCase()}-report`;
    const name = this.slug(this.calaName || fallbackName);
    const period = this.slug(this.reportPeriod);
    return `${name || fallbackName}${period ? `-${period}` : ""}.pdf`;
  }

  private totalProjectsText(): string {
    const statusKeys = ["ontime", "delayed", "completed"];
    const hasStatusTotals = statusKeys.some(
      (key) => this.overviewData && this.overviewData[key] != null
    );

    if (!hasStatusTotals) {
      return String(this.projects.length);
    }

    return this.integer(
      statusKeys.reduce(
        (total, key) => total + this.num(this.overviewData[key]),
        0
      )
    );
  }

  private progressStatus(expected: number, achieved: number): string {
    if (expected > 0 && achieved >= expected) {
      return "Completed";
    }
    if (achieved > 0) {
      return "In Progress";
    }
    return "Pending";
  }

  private normalizeOverview(source: AnyObj): AnyObj {
    const overview = { ...source };
    this.alias(overview, "total_land_aquire", [
      "total_land_acquire",
      "total_land_aquare",
      "land_aquire",
      "land_acquire",
    ]);
    this.alias(overview, "total_private_land_aquired", [
      "total_private_land_acquired",
      "total_area_aquired_PN",
      "total_area_aquired_pn",
    ]);
    this.alias(overview, "total_private_fund_deposition", [
      "fund_deposition",
      "private_fund_deposition",
    ]);
    this.alias(overview, "total_private_fund_distribution", [
      "fund_distribution",
      "private_fund_distribution",
    ]);
    return overview;
  }

  private normalizeBucket(source: AnyObj): AnyObj {
    const bucket = { ...(source || {}) };
    this.alias(bucket, "3A", ["3a"]);
    this.alias(bucket, "3H_area", ["3H", "3h_area"]);
    this.alias(bucket, "3H_money", ["3h_money"]);
    this.alias(bucket, "private_land_aquired", [
      "private_land_acquired",
      "private_area_aquired",
    ]);
    return bucket;
  }

  private alias(target: AnyObj, key: string, aliases: string[]): void {
    if (target[key] != null) {
      return;
    }

    const aliasKey = aliases.find((candidate) => target[candidate] != null);
    if (aliasKey) {
      target[key] = target[aliasKey];
    }
  }

  private value(
    source: AnyObj,
    keys: string | string[],
    fallback: any = null
  ): any {
    const candidates = Array.isArray(keys) ? keys : [keys];
    for (const key of candidates) {
      if (source && source[key] != null && source[key] !== "") {
        return source[key];
      }
    }
    return fallback;
  }

  private number(value: any, fallback = "0.00"): string {
    const numericValue = Number(value);
    return isFinite(numericValue) ? numericValue.toFixed(2) : fallback;
  }

  private integer(value: any): string {
    const numericValue = Number(value);
    return isFinite(numericValue) ? String(Math.round(numericValue)) : "0";
  }

  private percent(value: any): string {
    const numericValue = parseFloat(
      String(value == null ? "" : value).replace("%", "")
    );
    return isFinite(numericValue) ? `${numericValue.toFixed(2)}%` : "0.00%";
  }

  private percentNumber(value: string): number {
    const numericValue = parseFloat((value || "").replace("%", ""));
    return isFinite(numericValue) ? numericValue : 0;
  }

  private num(value: any): number {
    const numericValue = Number(value);
    return isFinite(numericValue) ? numericValue : 0;
  }

  private text(value: any, fallback = "N/A"): string {
    const result = value == null ? "" : String(value).trim();
    return result || fallback;
  }

  private chunk<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }
    return chunks;
  }

  private slug(value: string): string {
    return (value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
  }

  private isReportLoadFailure(
    value: CalaReportResponse | ReportLoadFailure
  ): value is ReportLoadFailure {
    return !!value && "message" in value && !("overview" in value);
  }

  private extractErrorMessage(error: any): string {
    const body = error?.error;
    if (body?.detail) {
      return String(body.detail);
    }
    if (body?.message) {
      return String(body.message);
    }
    return `${this.reportKind} report data could not be loaded.`;
  }

  private resetReportData(): void {
    this.overviewData = {};
    this.toBeCompleted = {};
    this.completedTillNow = {};
    this.inPercentage = {};
    this.projects = [];
    this.kpis = [];
    this.progressRows = [];
    this.summarySections = [];
    this.projectPages = [];
  }

  private async waitForImages(root: HTMLElement): Promise<void> {
    const images = Array.prototype.slice.call(
      root.querySelectorAll("img")
    ) as HTMLImageElement[];
    await Promise.all(
      images
        .filter((image) => !image.complete)
        .map(
          (image) =>
            new Promise<void>((resolve) => {
              image.onload = () => resolve();
              image.onerror = () => resolve();
            })
        )
    );
    await this.nextPaint();
  }

  private async waitForFonts(): Promise<void> {
    const documentWithFonts = document as Document & {
      fonts?: { ready: Promise<unknown> };
    };
    if (documentWithFonts.fonts?.ready) {
      await documentWithFonts.fonts.ready;
    }
    await this.nextPaint();
  }

  private async nextPaint(): Promise<void> {
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
  }

  ngOnDestroy(): void {
    this.clearAutoDownloadTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
